import axios from 'axios';

import { getApiBaseUrl } from '../utils/apiUtils';

// API Configuration
const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Generate or get session ID for anonymous users
const getSessionId = () => {
  let sessionId = localStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

// Request interceptor to add auth token and session ID
const isDebug = typeof window !== 'undefined' && (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1');
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Only log for non-polling requests to reduce console spam
      if (isDebug && !config.url?.includes('/whatsapp/conversations') && !config.url?.includes('/whatsapp/status')) {
        console.log('🔑 Adding token to request:', config.url);
      }
    } else {
      // Add session ID for anonymous users to track recently viewed products
      config.headers['x-session-id'] = getSessionId();
      if (isDebug) console.log('🔑 No token found, using session ID');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Only logout for authentication endpoints or if we're already authenticated
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      const hasToken = localStorage.getItem('authToken');
      const currentPath = window.location.pathname;
      
      if (isDebug) console.log('🔄 401 detected:', {
        url: error.config?.url,
        isAuthEndpoint,
        hasToken,
        currentPath
      });
      
      // Only logout for auth endpoints or profile verification
      if (isAuthEndpoint || error.config?.url?.includes('/auth/profile')) {
        if (isDebug) console.log('🔄 Logging out user due to 401 on auth endpoint');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        if (isDebug) console.log('🔄 401 on non-auth endpoint - not logging out automatically');
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  verifyOtp: (otpData) => api.post('/auth/verify-otp', otpData),
  resendOtp: (email) => api.post('/auth/resend-otp', { email }),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (userData) => api.put('/auth/profile', userData),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
  changePasswordWithOld: (passwordData) => api.post('/auth/change-password-with-old', passwordData),
  forgotPassword: (email, recaptchaToken) => api.post('/auth/forgot-password', { email, recaptchaToken }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// Contact API
export const contactAPI = {
  submit: (contactData) => api.post('/contact', contactData),
  getInquiries: (params) => api.get('/contact', { params }),
  getInquiry: (id) => api.get(`/contact/${id}`),
  updateInquiry: (id, data) => api.put(`/contact/${id}`, data),
  deleteInquiry: (id) => api.delete(`/contact/${id}`),
  getStats: () => api.get('/contact/stats/dashboard'),
  
  // Bulk actions
  bulkUpdateStatus: (inquiryIds, status) => api.put('/contact/bulk/status', { inquiryIds, status }),
  bulkUpdatePriority: (inquiryIds, priority) => api.put('/contact/bulk/priority', { inquiryIds, priority }),
  bulkDelete: (inquiryIds) => api.delete('/contact/bulk', { data: { inquiryIds } }),
};

// Appointment API
export const appointmentAPI = {
  submit: (appointmentData) => api.post('/appointments', appointmentData),
  getAll: (params) => api.get('/appointments', { params }),
  getById: (id) => api.get(`/appointments/${id}`),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  getStats: () => api.get('/appointments/stats'),
  confirm: (id) => api.post(`/appointments/${id}/confirm`),
  reschedule: (id, data) => api.post(`/appointments/${id}/reschedule`, data),
  cancel: (id, reason) => api.post(`/appointments/${id}/cancel`, { reason }),
  // Dashboard specific methods
  create: (appointmentData) => api.post('/appointments', appointmentData),
  getMyAppointments: (params) => api.get('/appointments/my-appointments', { params }),
  
  // Bulk operations
  bulkUpdateStatus: (appointmentIds, status) => api.put('/appointments/bulk/status', { appointmentIds, status }),
  bulkDelete: (appointmentIds) => api.delete('/appointments/bulk', { data: { appointmentIds } }),
};

// Not Found Page API
export const notFoundPageAPI = {
  getContent: () => api.get('/not-found-page/content'),
  getAdminContent: () => api.get('/not-found-page/admin'),
  updateContent: (data) => api.put('/not-found-page/content', data),
  resetContent: () => api.post('/not-found-page/reset'),
};

// Contact Form Options API
export const contactFormOptionsAPI = {
  getOptions: () => api.get('/contact-form-options'),
  getAdminOptions: () => api.get('/contact-form-options/admin'),
  createService: (data) => api.post('/contact-form-options/services', data),
  updateService: (id, data) => api.put(`/contact-form-options/services/${id}`, data),
  deleteService: (id) => api.delete(`/contact-form-options/services/${id}`),
  createBudget: (data) => api.post('/contact-form-options/budgets', data),
  updateBudget: (id, data) => api.put(`/contact-form-options/budgets/${id}`, data),
  deleteBudget: (id) => api.delete(`/contact-form-options/budgets/${id}`),
  createTimeline: (data) => api.post('/contact-form-options/timelines', data),
  updateTimeline: (id, data) => api.put(`/contact-form-options/timelines/${id}`, data),
  deleteTimeline: (id) => api.delete(`/contact-form-options/timelines/${id}`),
  
  // Bulk operations
  bulkUpdateStatus: (type, itemIds, is_active) => api.put(`/contact-form-options/${type}/bulk/status`, { itemIds, is_active }),
  bulkDelete: (type, itemIds) => api.delete(`/contact-form-options/${type}/bulk`, { data: { itemIds } }),
};

// Contact Form Fields API
export const contactFormFieldsAPI = {
  getFields: () => api.get('/contact-form-fields'),
  getAdminFields: () => api.get('/contact-form-fields/admin'),
  updateField: (id, data) => api.put(`/contact-form-fields/${id}`, data),
  updateFieldsBulk: (fields) => api.put('/contact-form-fields/bulk', { fields }),
  
  // Bulk operations
  bulkUpdateStatus: (fieldIds, is_active) => api.put('/contact-form-fields/bulk/status', { fieldIds, is_active }),
  bulkDelete: (fieldIds) => api.delete('/contact-form-fields/bulk', { data: { fieldIds } }),
};

// Maintenance Page API
export const maintenancePageAPI = {
  getContent: () => api.get('/maintenance-page/content'),
  getAdminContent: () => api.get('/maintenance-page/admin'),
  updateContent: (data) => api.put('/maintenance-page/content', data),
  resetContent: () => api.post('/maintenance-page/reset'),
};

// Quote API
export const quoteAPI = {
  generate: (quoteData) => api.post('/quotes', quoteData),
  create: (quoteData) => api.post('/quotes', quoteData),
  getAll: (params) => api.get('/quotes', { params }),
  getById: (id) => api.get(`/quotes/${id}`),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  delete: (id) => api.delete(`/quotes/${id}`),
  getStats: () => api.get('/quotes/stats'),
  downloadPDF: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  test: () => api.get('/quotes/test'),
  getByProject: (projectId) => api.get(`/quotes/project/${projectId}`),
  getMyQuotes: (params) => api.get('/quotes/my-quotes', { params }),
  
  // Bulk operations
  bulkUpdateStatus: (quoteIds, status) => api.put('/quotes/bulk/status', { quoteIds, status }),
  bulkDelete: (quoteIds) => api.delete('/quotes/bulk', { data: { quoteIds } }),
};

// Project API
export const projectAPI = {
  create: (projectData) => api.post('/projects', projectData),
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getStats: () => api.get('/projects/stats'),
  getMyProjects: (params) => api.get('/projects/my-projects', { params }),
  getProjectById: (id) => api.get(`/projects/${id}`),
  getProjectFiles: (projectId) => api.get(`/projects/${projectId}/files`),
  getProjectTasks: (projectId) => api.get(`/projects/${projectId}/tasks`),
  getProjectUpdates: (projectId) => api.get(`/projects/${projectId}/updates`),
  getProjectInvoices: (projectId) => api.get(`/projects/${projectId}/invoices`),
  getProjectPayments: (projectId) => api.get(`/projects/${projectId}/payments`),
  getProjectAppointments: (projectId) => api.get(`/projects/${projectId}/appointments`),
  
  // Bulk operations
  bulkUpdateStatus: (projectIds, status) => api.put('/projects/bulk/status', { projectIds, status }),
  bulkUpdateFeatured: (projectIds, featured) => api.put('/projects/bulk/featured', { projectIds, featured }),
  bulkDelete: (projectIds) => api.delete('/projects/bulk', { data: { projectIds } }),
};

// Project Types API
export const projectTypeAPI = {
  getAll: () => api.get('/project-types'),
  getAllAdmin: () => api.get('/project-types/all'),
  create: (data) => api.post('/project-types', data),
  update: (id, data) => api.put(`/project-types/${id}`, data),
  delete: (id) => api.delete(`/project-types/${id}`),
  
  // Bulk operations
  bulkUpdateStatus: (projectTypeIds, is_active) => api.put('/project-types/bulk/status', { projectTypeIds, is_active }),
  bulkDelete: (projectTypeIds) => api.delete('/project-types/bulk', { data: { projectTypeIds } }),
};



// Blog API
export const blogAPI = {
  // Public endpoints
  getAll: (params) => api.get('/blog', { params }),
  getPosts: (params) => api.get('/blog', { params }),
  getPost: (id) => api.get(`/blog/${id}`),
  getCategories: () => api.get('/blog/categories'),
  getTags: () => api.get('/blog/tags'),
  getRelatedPosts: (id) => api.get(`/blog/${id}/related`),
  
  // Admin endpoints
  createPost: (postData) => api.post('/blog/posts', postData),
  updatePost: (id, data) => api.put(`/blog/posts/${id}`, data),
  deletePost: (id) => api.delete(`/blog/posts/${id}`),
  getStats: () => api.get('/blog/stats'),
  
  // Comments
  getComments: (postId) => api.get(`/blog/posts/${postId}/comments`),
  addComment: (postId, comment) => api.post(`/blog/posts/${postId}/comments`, comment),
  updateComment: (commentId, data) => api.put(`/blog/comments/${commentId}`, data),
  deleteComment: (commentId) => api.delete(`/blog/comments/${commentId}`),
  
  // Bulk operations
  bulkUpdateStatus: (postIds, status) => api.put('/blog/bulk/status', { postIds, status }),
  bulkUpdateFeatured: (postIds, featured) => api.put('/blog/bulk/featured', { postIds, featured }),
  bulkDelete: (postIds) => api.delete('/blog/bulk', { data: { postIds } }),
};

// Product API
export const productAPI = {
  // Public endpoints
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getCategories: () => api.get('/products/categories'),
  getRelated: (id) => api.get(`/products/${id}/related`),
  
  // Admin endpoints
  create: (productData) => api.post('/products', productData),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getStats: () => api.get('/products/stats/dashboard'),
  validateSlug: (slug, exclude) => api.get('/products/validate-slug', { params: { slug, exclude } }),
  
  // Bulk actions
  bulkUpdateStatus: (productIds, status) => api.put('/products/bulk/status', { productIds, status }),
  bulkUpdateFeatured: (productIds, featured) => api.put('/products/bulk/featured', { productIds, featured }),
  bulkDelete: (productIds) => api.delete('/products/bulk', { data: { productIds } }),
  
  // Category bulk actions
  bulkUpdateCategoryStatus: (categoryIds, status) => api.put('/product-categories/bulk/status', { categoryIds, status }),
  bulkDeleteCategories: (categoryIds) => api.delete('/product-categories/bulk', { data: { categoryIds } }),
  
  // Reviews
  getReviews: (productId) => api.get(`/products/${productId}/reviews`),
  addReview: (productId, review) => api.post(`/products/${productId}/reviews`, review),
  updateReview: (reviewId, data) => api.put(`/products/reviews/${reviewId}`, data),
  deleteReview: (reviewId) => api.delete(`/products/reviews/${reviewId}`),
  
  // CodeCanyon integration
  fetchFromCodeCanyon: (url) => api.post('/products/fetch-codecanyon', { url }, { timeout: 120000 }), // 2 minutes timeout for Puppeteer
};

// Order API
export const orderAPI = {
  // Create order
  create: (orderData) => api.post('/orders', orderData),
  
  // User endpoints
  getMyOrders: (params) => api.get('/orders/my-orders', { params }),
  
  // Admin endpoints
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/id/${id}`),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  getStats: () => api.get('/orders/stats/summary'),
  
  // Bulk actions
  bulkUpdateStatus: (orderIds, status) => api.put('/orders/bulk/status', { orderIds, status }),
  bulkUpdatePaymentStatus: (orderIds, paymentStatus) => api.put('/orders/bulk/payment-status', { orderIds, paymentStatus }),
  bulkDelete: (orderIds) => api.delete('/orders/bulk', { data: { orderIds } }),
};

// User API
export const userAPI = {
  // Admin endpoints
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post('/users', userData),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getStats: () => api.get('/users/stats'),
  resetPassword: (id, password) => api.put(`/users/${id}/password`, { password }),
  
  // User endpoints
  getProfile: () => api.get('/users/profile'),
  getCrmCredentials: () => api.get('/users/crm-credentials'),
  
  // Bulk actions
  bulkUpdateStatus: (userIds, status) => api.put('/users/bulk/status', { userIds, status }),
  bulkUpdateRole: (userIds, role) => api.put('/users/bulk/role', { userIds, role }),
  bulkDelete: (userIds) => api.delete('/users/bulk', { data: { userIds } }),
};

// Testimonial API
export const testimonialAPI = {
  // Public endpoints
  getAll: (params) => api.get('/testimonials', { params }),
  submit: (testimonialData) => api.post('/testimonials/submit', testimonialData),
  
  // Admin endpoints
  getById: (id) => api.get(`/testimonials/${id}`),
  create: (testimonialData) => api.post('/testimonials', testimonialData),
  update: (id, data) => api.put(`/testimonials/${id}`, data),
  delete: (id) => api.delete(`/testimonials/${id}`),
  getStats: () => api.get('/testimonials/stats/overview'),
  
  // Bulk actions
  bulkUpdateStatus: (testimonialIds, status) => api.put('/testimonials/bulk/status', { testimonialIds, status }),
  bulkUpdateFeatured: (testimonialIds, featured) => api.put('/testimonials/bulk/featured', { testimonialIds, featured }),
  bulkDelete: (testimonialIds) => api.delete('/testimonials/bulk', { data: { testimonialIds } }),
};

// Promotions API
export const promotionsAPI = {
  // Public endpoints
  getAll: (params) => api.get('/promotions', { params }),
  getById: (id) => api.get(`/promotions/${id}`),
  
  // Admin endpoints
  create: (promotionData) => api.post('/promotions', promotionData),
  update: (id, data) => api.put(`/promotions/${id}`, data),
  delete: (id) => api.delete(`/promotions/${id}`),
  
  // Bulk operations
  bulkUpdateStatus: (promotionIds, is_active) => api.put('/promotions/bulk/status', { promotionIds, is_active }),
  bulkDelete: (promotionIds) => api.delete('/promotions/bulk', { data: { promotionIds } }),
  
  // Sync operations
  getSyncInfo: (id) => api.get(`/promotions/${id}/sync-info`),
  syncPromotion: (id) => api.post(`/promotions/${id}/sync`),
};

// Public Settings API
export const publicSettingsAPI = {
  getSettings: () => api.get('/public/settings'),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
  getNotifications: (params) => api.get('/admin/notifications', { params }),
  markNotificationAsRead: (id) => api.put(`/admin/notifications/${id}/read`),
  markAllNotificationsAsRead: () => api.put('/admin/notifications/read-all'),
  getActivity: (params) => api.get('/admin/activity', { params }),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', { settings }),
  getSystemInfo: () => api.get('/admin/system-info'),
  createBackup: () => api.post('/admin/backup'),
  getHealth: () => api.get('/admin/health'),
  changePassword: (data) => api.put('/admin/change-password', data),
  
  // Special Days API
  getSpecialDays: () => api.get('/special-days'),
  getSpecialDay: (id) => api.get(`/special-days/${id}`),
  createSpecialDay: (data) => api.post('/special-days', data),
  updateSpecialDay: (id, data) => api.put(`/special-days/${id}`, data),
  deleteSpecialDay: (id) => api.delete(`/special-days/${id}`),
  bulkDeleteSpecialDays: (ids) => api.delete('/special-days/bulk/delete', { data: { ids } }),
  bulkSyncSpecialDays: (ids) => api.post('/special-days/bulk/sync', { ids }),
  bulkSyncOnSpecialDays: (ids) => api.post('/special-days/bulk/sync-on', { ids }),
  bulkSyncOffSpecialDays: (ids) => api.post('/special-days/bulk/sync-off', { ids }),
  toggleSpecialDay: (id, field) => api.patch(`/special-days/${id}/toggle`, { field }),
  getSpecialDaySyncInfo: (id) => api.get(`/special-days/${id}/sync-info`),
  syncSpecialDay: (id) => api.post(`/special-days/${id}/sync`),
  testSpecialDaySync: (id) => api.get(`/special-days/${id}/test-sync`),
  
  // S3 Management API
  getS3Settings: () => api.get('/s3/settings'),
  updateS3Settings: (settings) => api.put('/s3/settings', { settings }),
  testS3Connection: (settings) => api.post('/s3/test-connection', { settings }),
  getS3Files: (params) => api.get('/s3/files', { params }),
  uploadS3File: (formData) => api.post('/s3/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  deleteS3File: (id) => api.delete(`/s3/files/${id}`),
  bulkDeleteS3Files: (fileIds) => api.post('/s3/files/bulk-delete', { fileIds }),
  getS3Stats: () => api.get('/s3/stats'),
  migrateS3Files: (fileIds, targetStorage) => api.post('/s3/migrate', { fileIds, targetStorage }),
  
  // AI API
  getAISettings: () => api.get('/ai/settings'),
  updateAISettings: (settings) => api.post('/ai/settings', settings),
  testAIConnection: () => api.post('/ai/test-connection'),
  generateContent: (data) => api.post('/ai/generate-content', data),
  generateImage: (data) => {
    // Create a specific axios instance for image generation with longer timeout
    const imageApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000, // 60 seconds timeout for image generation
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Add auth token to image generation requests
    const token = localStorage.getItem('authToken');
    if (token) {
      imageApi.defaults.headers.Authorization = `Bearer ${token}`;
    }
    
    return imageApi.post('/ai/generate-image', data);
  },
  analyzeSentiment: (data) => api.post('/ai/analyze-sentiment', data),
  translateText: (data) => api.post('/ai/translate', data),
  generateRecommendations: (data) => api.post('/ai/generate-recommendations', data),
  analyzeAnalytics: (data) => api.post('/ai/analyze-analytics', data),
  getConversations: (params) => api.get('/ai/conversations', { params }),
  deleteConversation: (conversationId) => api.delete(`/ai/conversations/${conversationId}`),
  
  // WhatsApp Conversation Management
  deleteWhatsAppConversation: async (id) => {
    const res = await api.delete(`/whatsapp/conversations/${id}`);
    return res.data;
  },
  bulkDeleteWhatsAppConversations: async (conversationIds) => {
    const res = await api.post('/whatsapp/conversations/bulk-delete', { conversationIds });
    return res.data;
  },
  
  // AI Management
  getAiSettings: () => api.get('/admin/ai-settings'),
  updateAiSettings: (settings) => api.put('/admin/ai-settings', settings),
  testAiConnection: () => api.post('/admin/ai-settings/test-connection'),
  
  // Floating Chat Management
  getFloatingChatSettings: () => api.get('/admin/floating-chat-settings'),
  updateFloatingChatSettings: (settings) => api.put('/admin/floating-chat-settings', settings),
  
  // User Management
};

// WhatsApp API
export const whatsappAPI = {
  // General WhatsApp API
  getSettings: () => api.get('/whatsapp/settings'),
  updateSettings: (settings) => api.put('/whatsapp/settings', settings),
  sendMessage: (data) => api.post('/whatsapp/send/text', data),
  sendTemplate: (data) => api.post('/whatsapp/send-template', data),
  sendMedia: (data) => api.post('/whatsapp/send-media', data),
  sendDocument: (data) => api.post('/whatsapp/send-document', data),
  sendAudio: (data) => api.post('/whatsapp/send-audio', data),
  sendVideo: (data) => api.post('/whatsapp/send-video', data),
  sendLocation: (data) => api.post('/whatsapp/send-location', data),
  sendContact: (data) => api.post('/whatsapp/send-contact', data),
  logWebsiteMessage: (data) => api.post('/whatsapp/log-website-message', data),
  sendReaction: (messageId, emoji) => api.post(`/whatsapp/messages/${messageId}/reactions`, { emoji }),
  removeReaction: (messageId) => api.delete(`/whatsapp/messages/${messageId}/reactions`),
  getMessageStatus: (messageId) => api.get(`/whatsapp/messages/${messageId}/status`),
  markMessageAsRead: (messageId) => api.post(`/whatsapp/messages/${messageId}/mark-as-read`),
  
  // Conversation Management
  getConversationMessages: (phoneNumber) => api.get(`/whatsapp/conversations/${phoneNumber}/messages`),
  getConversations: () => api.get('/whatsapp/conversations'),
  getConversation: (phoneNumber) => api.get(`/whatsapp/conversations/${phoneNumber}`),
  getMessageStatuses: async (phoneNumber) => {
    const res = await api.get(`/whatsapp/conversations/${phoneNumber}/message-statuses`);
    return res.data;
  },
  
  // Business Profile
  getBusinessProfile: () => api.get('/whatsapp/business/profile'),
  updateBusinessProfile: (profileData) => api.put('/whatsapp/business/profile', profileData),
  
  // Phone Numbers
  getPhoneNumbers: () => api.get('/whatsapp/phone-numbers'),
  registerPhoneNumber: (phoneNumberId, pin) => api.post(`/whatsapp/phone-numbers/${phoneNumberId}/register`, { pin }),
  
  // Media Management
  getMediaFile: (mediaId) => api.get(`/whatsapp/media/${mediaId}`),
  deleteMediaFile: (mediaId) => api.delete(`/whatsapp/media/${mediaId}`),
  uploadMedia: (formData) => api.post('/whatsapp/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  

  
  // Templates
  getTemplates: () => api.get('/whatsapp/templates'),
  createTemplate: (data) => api.post('/whatsapp/templates', data),
  updateTemplate: (id, data) => api.put(`/whatsapp/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/whatsapp/templates/${id}`),
  
  // Auto Reply
  getFaqs: () => api.get('/whatsapp/faqs'),
  createFaq: (data) => api.post('/whatsapp/faqs', data),
  updateFaq: (id, data) => api.put(`/whatsapp/faqs/${id}`, data),
  deleteFaq: (id) => api.delete(`/whatsapp/faqs/${id}`),
  
  getAutoReplies: () => api.get('/whatsapp/auto-replies'),
  createAutoReply: (data) => api.post('/whatsapp/auto-replies', data),
  updateAutoReply: (id, data) => api.put(`/whatsapp/auto-replies/${id}`, data),
  deleteAutoReply: (id) => api.delete(`/whatsapp/auto-replies/${id}`),
  testAiResponse: (data) => api.post('/whatsapp/test-ai-response', data),
  getAiStats: () => api.get('/whatsapp/ai-stats'),
  
  // AI Assistants
  getAiAssistants: () => api.get('/whatsapp/ai-assistants'),
  getAiAssistant: (id) => api.get(`/whatsapp/ai-assistants/${id}`),
  createAiAssistant: (data) => api.post('/whatsapp/ai-assistants', data),
  updateAiAssistant: (id, data) => api.put(`/whatsapp/ai-assistants/${id}`, data),
  deleteAiAssistant: (id) => api.delete(`/whatsapp/ai-assistants/${id}`),
  testAiAssistant: (id, data) => api.post(`/whatsapp/ai-assistants/${id}/test`, data),
  getAiAssistantsStats: () => api.get('/whatsapp/ai-assistants-stats'),
  
  getAutoReplySettings: (cacheBuster) => {
    const url = cacheBuster ? `/whatsapp/auto-reply-settings${cacheBuster}` : '/whatsapp/auto-reply-settings';
    return api.get(url, { headers: { 'Cache-Control': 'no-cache' } });
  },
  updateAutoReplySettings: (settings) => api.post('/whatsapp/auto-reply-settings', settings),
  
  // Bulk Actions
  bulkDelete: (endpoint, ids) => api.post(`/whatsapp/${endpoint}/bulk-delete`, { ids }),
  bulkUpdate: (endpoint, ids, updates) => api.post(`/whatsapp/${endpoint}/bulk-update`, { ids, updates }),
  bulkExport: (endpoint, ids) => api.post(`/whatsapp/${endpoint}/bulk-export`, { ids }),
  bulkImport: (endpoint, formData) => api.post(`/whatsapp/${endpoint}/bulk-import`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // E-commerce
  getEcommerceSettings: () => api.get('/whatsapp/ecommerce-settings'),
  updateEcommerceSettings: (settings) => api.put('/whatsapp/ecommerce-settings', settings),
  trackAbandonedCart: (data) => api.post('/whatsapp/ecommerce/track-cart', data),
  sendOrderNotification: (data) => api.post('/whatsapp/ecommerce/order-notification', data),
  syncCatalogItem: (data) => api.post('/whatsapp/ecommerce/sync-catalog', data),
  getAbandonedCarts: () => api.get('/whatsapp/ecommerce/abandoned-carts'),
  getOrderNotifications: () => api.get('/whatsapp/ecommerce/order-notifications'),
  getCatalogItems: () => api.get('/whatsapp/ecommerce/catalog-items'),
  getEcommerceAnalytics: () => api.get('/whatsapp/ecommerce/analytics'),
  
  // Marketing
  getMarketingCampaigns: () => api.get('/whatsapp/marketing-campaigns'),
  createMarketingCampaign: (data) => api.post('/whatsapp/marketing-campaigns', data),
  updateMarketingCampaign: (id, data) => api.put(`/whatsapp/marketing-campaigns/${id}`, data),
  deleteMarketingCampaign: (id) => api.delete(`/whatsapp/marketing-campaigns/${id}`),
  createSegment: (data) => api.post('/whatsapp/marketing/segments', data),
  executeCampaign: (campaignId) => api.post(`/whatsapp/marketing/campaigns/${campaignId}/execute`),
  createABTest: (data) => api.post('/whatsapp/marketing/ab-tests', data),
  getMarketingSettings: () => api.get('/whatsapp/marketing/settings'),
  updateMarketingSettings: (settings) => api.put('/whatsapp/marketing/settings', settings),
  
  // Broadcast
  sendBroadcast: (data) => api.post('/whatsapp/broadcast', data),
  uploadContacts: (formData) => api.post('/whatsapp/contacts/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Contacts Management (normalized responses)
  getContacts: async (params) => {
    const noCacheSuffix = `_=${Date.now()}`;
    const joined = params ? `${params}&${noCacheSuffix}` : noCacheSuffix;
    const res = await api.get(`/whatsapp/contacts?${joined}`, { headers: { 'Cache-Control': 'no-cache' } });
    return res.data; // { success, data, pagination }
  },
  getContact: async (id) => {
    const res = await api.get(`/whatsapp/contacts/${id}`);
    return res.data;
  },
  createContact: async (data) => {
    const res = await api.post('/whatsapp/contacts', data);
    return res.data;
  },
  updateContact: async (id, data) => {
    const res = await api.put(`/whatsapp/contacts/${id}`, data);
    return res.data;
    },
  deleteContact: async (id) => {
    const res = await api.delete(`/whatsapp/contacts/${id}`);
    return res.data;
  },
  bulkUpdateContacts: async (contactIds, updates) => {
    const res = await api.post('/whatsapp/contacts/bulk-update', { contactIds, updates });
    return res.data;
  },
  bulkDeleteContacts: async (contactIds) => {
    const res = await api.post('/whatsapp/contacts/bulk-delete', { contactIds });
    return res.data;
  },
  // Conversation Management
  deleteConversation: async (id) => {
    const res = await api.delete(`/whatsapp/conversations/${id}`);
    return res.data;
  },
  bulkDeleteConversations: async (conversationIds) => {
    const res = await api.post('/whatsapp/conversations/bulk-delete', { conversationIds });
    return res.data;
  },
  uploadContactsCSV: async (formData) => {
    const res = await api.post('/whatsapp/contacts/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  },
  exportContactsCSV: (params) => api.get(`/whatsapp/contacts/export-csv?${params}`, { responseType: 'blob' }),
  downloadSampleCSV: () => api.get('/whatsapp/contacts/sample-csv', { responseType: 'blob' }),
  getContactStats: async () => {
    const res = await api.get('/whatsapp/contacts/stats');
    return res.data;
  },
  getContactTypes: async () => {
    const res = await api.get('/whatsapp/contacts/types');
    return res.data;
  },
  
  // Analytics
  getAnalytics: (params) => api.get('/whatsapp/analytics', { params }),
  getChatHistory: (params) => api.get('/whatsapp/chat-history', { params }),
  
  // Advanced Features Analytics
  getOtpAnalytics: (period) => api.get(`/whatsapp/analytics/otp/${period}`),
  getVoiceAnalytics: (period) => api.get(`/whatsapp/analytics/voice/${period}`),
  getPaymentAnalytics: (period) => api.get(`/whatsapp/analytics/payment/${period}`),
  getAiAnalytics: (period) => api.get(`/whatsapp/analytics/ai/${period}`),
  getTicketAnalytics: (period) => api.get(`/whatsapp/analytics/tickets/${period}`),
  getGroupAnalytics: (groupId, period) => api.get(`/whatsapp/analytics/groups/${groupId}/${period}`),
  
  // OTP Verification
  validateNumber: (data) => api.post('/whatsapp/validate-number', data),
  sendOtp: (data) => api.post('/whatsapp/otp/send', data),
  verifyOtp: (data) => api.post('/whatsapp/otp/verify', data),
  resendOtp: (data) => api.post('/whatsapp/otp/resend', data),
  
  // Voice Processing
  processVoiceNote: (data) => api.post('/whatsapp/voice/process', data),
  getVoiceNotes: (phoneNumber, limit) => api.get(`/whatsapp/voice/notes/${phoneNumber}`, { params: { limit } }),
  
  // Payment Links
  createPaymentLink: (data) => api.post('/whatsapp/payment/create-link', data),
  
  // AI Advanced Features
  processMessageWithAI: (data) => api.post('/whatsapp/ai/process-message', data),
  analyzeSentiment: (data) => api.post('/whatsapp/ai/sentiment', data),
  autoTranslate: (data) => api.post('/whatsapp/ai/translate', data),
  
  // Advanced Features
  createTicket: (data) => api.post('/whatsapp/tickets/create', data),
  createGroup: (data) => api.post('/whatsapp/groups/create', data),
  
  // OTP Features
  sendOtp: (data) => api.post('/whatsapp/otp/send', data),
  verifyOtp: (data) => api.post('/whatsapp/otp/verify', data),
  
  // Voice Processing
  processVoiceNote: (data) => api.post('/whatsapp/voice/process', data),
  
  // Payment Links
  createPaymentLink: (data) => api.post('/whatsapp/payment/create-link', data),
  
  // Advanced Ticketing
  createTicket: (data) => api.post('/whatsapp/tickets/create', data),
  updateTicketStatus: (id, data) => api.put(`/whatsapp/tickets/${id}/status`, data),
  addAgent: (data) => api.post('/whatsapp/agents/add', data),
  
  // Groups Automation
  createGroup: (data) => api.post('/whatsapp/groups/create', data),
  sendGroupMessage: (groupId, data) => api.post(`/whatsapp/groups/${groupId}/send-message`, data),
  addGroupMember: (groupId, data) => api.post(`/whatsapp/groups/${groupId}/members/add`, data),
  createGroupEvent: (groupId, data) => api.post(`/whatsapp/groups/${groupId}/events/create`, data),
  createGroupSurvey: (groupId, data) => api.post(`/whatsapp/groups/${groupId}/surveys/create`, data),
  
  // Advanced Analytics
  getOtpAnalytics: (period) => api.get(`/whatsapp/analytics/otp/${period}`),
  getVoiceAnalytics: (period) => api.get(`/whatsapp/analytics/voice/${period}`),
  getPaymentAnalytics: (period) => api.get(`/whatsapp/analytics/payment/${period}`),
  getAiAnalytics: (period) => api.get(`/whatsapp/analytics/ai/${period}`),
  getTicketAnalytics: (period) => api.get(`/whatsapp/analytics/tickets/${period}`),
  getGroupAnalytics: (groupId, period) => api.get(`/whatsapp/analytics/groups/${groupId}/${period}`),
};

// Payment API
export const paymentAPI = {
  createOrder: (orderData) => api.post('/payments/create-order', orderData),
  verifyPayment: (paymentData) => api.post('/payments/verify', paymentData),
  getOrders: (params) => api.get('/payments/orders', { params }),
  getOrder: (id) => api.get(`/payments/orders/${id}`),
  processRefund: (paymentId, data) => api.post('/payments/refund', { paymentId, ...data }),
  getStats: () => api.get('/payments/stats'),
  // Dashboard specific methods
  create: (paymentData) => api.post('/payments', paymentData),
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  getMyPayments: (params) => api.get('/payments/my-payments', { params }),
};

// Payment Methods API
export const paymentMethodsAPI = {
  getConfig: () => api.get('/payment-methods/config'),
  updateCashfreeConfig: (config) => api.put('/payment-methods/cashfree', config),
  updateRazorpayConfig: (config) => api.put('/payment-methods/razorpay', config),
  updatePhonepeConfig: (config) => api.put('/payment-methods/phonepe', config),
  updateOfflineConfig: (config) => api.put('/payment-methods/offline', config),
  updateCodConfig: (config) => api.put('/payment-methods/cod', config),
  testCashfreeConnection: () => api.post('/payment-methods/cashfree/test'),
  getActiveMethods: () => api.get('/payment-methods/active'),
};

// Payment Options API
export const paymentOptionsAPI = {
  getConfig: () => api.get('/payment-options/config'),
  updateConfig: (config) => api.put('/payment-options/config', config),
  validateCoupon: (couponCode, paymentType, productId) => api.post('/payment-options/validate-coupon', {
    coupon_code: couponCode,
    payment_type: paymentType,
    product_id: productId
  }),
};

// Upload API
export const uploadAPI = {
  uploadImage: (file, options = {}) => {
    const formData = new FormData();
    formData.append('image', file);
    if (options.resize) formData.append('resize', options.resize);
    if (options.quality) formData.append('quality', options.quality);
    
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  uploadImages: (files, options = {}) => {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    if (options.resize) formData.append('resize', options.resize);
    if (options.quality) formData.append('quality', options.quality);
    
    return api.post('/upload/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('document', file);
    
    return api.post('/upload/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    
    return api.post('/upload/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  uploadAnimation: (file) => {
    const formData = new FormData();
    formData.append('animation', file);
    
    return api.post('/upload/animation', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  uploadProduct: (file) => {
    const formData = new FormData();
    formData.append('product', file);
    
    return api.post('/upload/product', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  uploadVideo: (file) => {
    const formData = new FormData();
    formData.append('video', file);
    
    return api.post('/upload/video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  getFiles: (params) => api.get('/upload/files', { params }),
  deleteFile: (id) => api.delete(`/upload/files/${id}`),
  getStats: () => api.get('/upload/stats'),
};

// Newsletter API
export const newsletterAPI = {
  // Public endpoints
  subscribe: (data) => api.post('/newsletter/subscribe', data),
  unsubscribe: (data) => api.post('/newsletter/unsubscribe', data),
  
  // Admin endpoints
  getSubscribers: (params) => api.get('/newsletter/subscribers', { params }),
  addSubscriber: (data) => api.post('/newsletter/subscribers', data),
  updateSubscriber: (id, data) => api.put(`/newsletter/subscribers/${id}`, data),
  deleteSubscriber: (id) => api.delete(`/newsletter/subscribers/${id}`),
  
  // Bulk subscriber actions
  bulkUpdateSubscriberStatus: (subscriberIds, status) => api.put('/newsletter/subscribers/bulk/status', { subscriberIds, status }),
  bulkDeleteSubscribers: (subscriberIds) => api.delete('/newsletter/subscribers/bulk', { data: { subscriberIds } }),
  
  // Email sending
  sendIndividualEmail: (data) => api.post('/newsletter/send-individual', data),
  sendBulkEmail: (data) => api.post('/newsletter/send-bulk', data),
  
  // Import/Export
  importSubscribers: (data) => api.post('/newsletter/import', data),
  exportSubscribers: (params) => api.get('/newsletter/export', { params }),
  
  // Templates
  getTemplates: () => api.get('/newsletter/templates'),
  createTemplate: (data) => api.post('/newsletter/templates', data),
  
  // Campaigns
  getCampaigns: (params) => api.get('/newsletter/campaigns', { params }),
  
  // Statistics
  getStats: () => api.get('/newsletter/stats'),
  // Defaults
  getDefaults: () => api.get('/newsletter/defaults'),
  updateDefaults: (data) => api.put('/newsletter/defaults', data),
  saveDefaults: (data) => api.post('/newsletter/defaults', data),
};

// Services API
export const servicesAPI = {
  // Public endpoints
  getAll: (params) => api.get('/services', { params }),
  getById: (id) => api.get(`/services/${id}`),
  getCategories: () => api.get('/services/categories/list'),
  
  // Admin endpoints
  create: (serviceData) => api.post('/services', serviceData),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  toggleStatus: (id, status) => api.patch(`/services/${id}/status`, { status }),
  toggleFeatured: (id, featured) => api.patch(`/services/${id}/featured`, { featured }),
  getStats: () => api.get('/services/stats/overview'),
  
  // Bulk operations
  bulkUpdateStatus: (serviceIds, status) => api.put('/services/bulk/status', { serviceIds, status }),
  bulkUpdateFeatured: (serviceIds, featured) => api.put('/services/bulk/featured', { serviceIds, featured }),
  bulkDelete: (serviceIds) => api.delete('/services/bulk', { data: { serviceIds } }),
};



// Portfolio API
export const portfolioAPI = {
  getAll: (params = {}) => api.get('/portfolio', { params }),
  getOne: (id) => api.get(`/portfolio/${id}`),
  create: (data) => api.post('/portfolio', data),
  update: (id, data) => api.put(`/portfolio/${id}`, data),
  delete: (id) => api.delete(`/portfolio/${id}`),
  
  // Bulk operations
  bulkUpdateStatus: (portfolioIds, status) => api.put('/portfolio/bulk/status', { portfolioIds, status }),
  bulkUpdateFeatured: (portfolioIds, featured) => api.put('/portfolio/bulk/featured', { portfolioIds, featured }),
  bulkDelete: (portfolioIds) => api.delete('/portfolio/bulk', { data: { portfolioIds } }),
};

// Homepage Content API
export const homepageAPI = {
  // Public endpoints
  getContent: () => api.get('/homepage'),
  getSection: (section) => api.get(`/homepage/section/${section}`),
  
  // Admin endpoints
  updateContent: (data) => {
    if (isDebug) {
      console.log('🔍 homepageAPI.updateContent called');
      console.log('Data to send:', data);
      console.log('Token exists:', !!localStorage.getItem('authToken'));
    }
    return api.put('/homepage', data);
  },
  updateSection: (section, data) => api.put(`/homepage/section/${section}`, data),
};

// Homepage Data API
export const homepageDataAPI = {
  getContent: () => api.get('/homepage-data'),
  updateContent: (data) => api.put('/homepage-data', data)
};

// About API
export const aboutAPI = {
  // Public endpoints
  getContent: () => api.get('/about'),
  getTeam: () => api.get('/about/team'),
  getDocuments: () => api.get('/about/documents'),
  
  // Admin endpoints
  updateContent: (data) => api.put('/about', data),
  getAllTeamMembers: () => api.get('/about/team/all'),
  createTeamMember: (data) => api.post('/about/team', data),
  updateTeamMember: (id, data) => api.put(`/about/team/${id}`, data),
  deleteTeamMember: (id) => api.delete(`/about/team/${id}`),
  
  // Documents management
  getAllDocuments: () => api.get('/about/documents/all'),
  createDocument: (data) => api.post('/about/documents', data),
  updateDocument: (id, data) => api.put(`/about/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/about/documents/${id}`),
};

// Products Page API
export const productsPageAPI = {
  getContent: () => api.get('/products-page'),
  updateContent: (data) => api.put('/products-page', data),
};

// Portfolio Page API
export const portfolioPageAPI = {
  getContent: () => api.get('/portfolio-page'),
  updateContent: (data) => api.put('/portfolio-page', data),
};

// Blog Page API
export const blogPageAPI = {
  getContent: () => api.get('/blog/content'),
  updateContent: (data) => api.put('/blog/content', data),
};

// Contact Page API
export const contactPageAPI = {
  getContent: () => api.get('/contact-page/content'),
  getAdmin: () => api.get('/contact-page/admin'),
  updateContent: (data) => api.put('/contact-page/content', data),
};

// Quote Page API
export const quotePageAPI = {
  getContent: () => api.get('/quotes/content'),
  updateContent: (data) => api.put('/quotes/content', data),
};

// Quote Steps API
export const quoteStepsAPI = {
  getAll: () => api.get('/quote-steps'),
  getAdmin: () => api.get('/quote-steps/admin'),
  create: (data) => api.post('/quote-steps', data),
  update: (id, data) => api.put(`/quote-steps/${id}`, data),
  delete: (id) => api.delete(`/quote-steps/${id}`),
  reorder: (steps) => api.post('/quote-steps/reorder', { steps }),
};

// Quote Content API
export const quoteContentAPI = {
  // Project Types
  getProjectTypes: () => api.get('/quote-content/project-types'),
  getProjectTypesAdmin: () => api.get('/quote-content/project-types/admin'),
  createProjectType: (data) => api.post('/quote-content/project-types', data),
  updateProjectType: (id, data) => api.put(`/quote-content/project-types/${id}`, data),
  deleteProjectType: (id) => api.delete(`/quote-content/project-types/${id}`),
  
  // Features
  getFeatures: (projectTypeId) => api.get(`/quote-content/features/${projectTypeId}`),
  getAllFeatures: () => api.get('/quote-content/features'),
  createFeature: (data) => api.post('/quote-content/features', data),
  updateFeature: (id, data) => api.put(`/quote-content/features/${id}`, data),
  deleteFeature: (id) => api.delete(`/quote-content/features/${id}`),
  
  // Timelines
  getTimelines: () => api.get('/quote-content/timelines'),
  getTimelinesAdmin: () => api.get('/quote-content/timelines/admin'),
  createTimeline: (data) => api.post('/quote-content/timelines', data),
  updateTimeline: (id, data) => api.put(`/quote-content/timelines/${id}`, data),
  deleteTimeline: (id) => api.delete(`/quote-content/timelines/${id}`),
  
  // Complexity Levels
  getComplexityLevels: () => api.get('/quote-content/complexity-levels'),
  getComplexityLevelsAdmin: () => api.get('/quote-content/complexity-levels/admin'),
  createComplexityLevel: (data) => api.post('/quote-content/complexity-levels', data),
  updateComplexityLevel: (id, data) => api.put(`/quote-content/complexity-levels/${id}`, data),
  deleteComplexityLevel: (id) => api.delete(`/quote-content/complexity-levels/${id}`),
};

// PDF Templates API
export const pdfTemplatesAPI = {
  getAll: () => api.get('/pdf-templates/admin'),
  getActive: () => api.get('/pdf-templates/active'),
  create: (data) => api.post('/pdf-templates', data),
  update: (id, data) => api.put(`/pdf-templates/${id}`, data),
  delete: (id) => api.delete(`/pdf-templates/${id}`),
  activate: (id) => api.post(`/pdf-templates/${id}/activate`),
  // Bulk operations
  bulkUpdateStatus: (templateIds, is_active) => api.put('/pdf-templates/bulk/status', { templateIds, is_active }),
  bulkDelete: (templateIds) => api.delete('/pdf-templates/bulk', { data: { templateIds } }),
};

// PDF Generation API
export const pdfGenerationAPI = {
  generate: (data) => api.post('/pdf-generation/generate', data, {
    responseType: 'blob'
  }),
  generateOrderPDF: (orderId) => api.post(`/pdf-generation/order/${orderId}`, {}, {
    responseType: 'blob'
  }),
  generateAppointmentPDF: (appointmentId) => api.post(`/pdf-generation/appointment/${appointmentId}`, {}, {
    responseType: 'blob'
  })
};

// Pages API
export const pagesAPI = {
  getAll: () => api.get('/pages/admin'),
  getByType: (pageType) => api.get(`/pages/${pageType}`),
  create: (data) => api.post('/pages', data),
  update: (id, data) => api.put(`/pages/${id}`, data),
  updateByType: (pageType, data) => api.put(`/pages/type/${pageType}`, data),
  delete: (id) => api.delete(`/pages/${id}`)
};

// Utility functions
export const apiUtils = {
  // Handle API errors
  handleError: (error) => {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || 'An error occurred';
      return {
        success: false,
        message,
        status: error.response.status,
        data: error.response.data,
      };
    } else if (error.request) {
      // Network error
      return {
        success: false,
        message: 'Network error. Please check your connection.',
        status: 0,
      };
    } else {
      // Other error
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        status: 0,
      };
    }
  },

  // Format API response
  formatResponse: (response) => {
    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || 'Success',
      status: response.status,
    };
  },

  // Build query string from params
  buildQueryString: (params) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    return searchParams.toString();
  },

  // Get file URL
  getFileUrl: (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith('http')) return filePath;
    return `${API_BASE_URL.replace('/api', '')}${filePath}`;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },

  // Get current user
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Set authentication data
  setAuthData: (token, user) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));
  },

  // Clear authentication data
  clearAuthData: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  // Check user role
  hasRole: (role) => {
    const user = apiUtils.getCurrentUser();
    return user?.role === role;
  },

  // Check if user is admin
  isAdmin: () => {
    return apiUtils.hasRole('admin');
  },

  // Format currency (deprecated - use CurrencyContext instead)
  formatCurrency: (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  },

  // Format date
  formatDate: (date, options = {}) => {
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return new Date(date).toLocaleDateString('en-IN', { ...defaultOptions, ...options });
  },

  // Format relative time
  formatRelativeTime: (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  },

  // Debounce function for search
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  // Validate email
  isValidEmail: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  // Validate phone
  isValidPhone: (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  },

  // Generate random ID
  generateId: () => {
    return Math.random().toString(36).substr(2, 9);
  },

  // Truncate text
  truncateText: (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  },

  // Get initials from name
  getInitials: (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substr(0, 2);
  },

  // Calculate reading time
  calculateReadingTime: (text) => {
    const wordsPerMinute = 200;
    const words = text.split(' ').length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  },

  // Generate slug from title
  generateSlug: (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  },
};

// Footer API
export const footerAPI = {
  getContent: () => api.get('/footer'),
  getAdminContent: () => api.get('/footer/admin'),
  updateSettings: (data) => api.put('/footer/settings', data),
  createSection: (data) => api.post('/footer/sections', data),
  updateSection: (id, data) => api.put(`/footer/sections/${id}`, data),
  deleteSection: (id) => api.delete(`/footer/sections/${id}`),
  createLink: (data) => api.post('/footer/links', data),
  updateLink: (id, data) => api.put(`/footer/links/${id}`, data),
  deleteLink: (id) => api.delete(`/footer/links/${id}`),
  createSocialLink: (data) => api.post('/footer/social-links', data),
  updateSocialLink: (id, data) => api.put(`/footer/social-links/${id}`, data),
  deleteSocialLink: (id) => api.delete(`/footer/social-links/${id}`),
};



// Quotation API
export const quotationAPI = {
  create: (quotationData) => api.post('/quotations', quotationData),
  getAll: (params) => api.get('/quotations', { params }),
  getById: (id) => api.get(`/quotations/${id}`),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  getMyQuotations: (params) => api.get('/quotations/my-quotations', { params }),
  downloadPDF: (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' }),
  getStats: () => api.get('/quotations/stats'),
  // Bulk operations
  bulkUpdateStatus: (quotationIds, status) => api.put('/quotations/bulk/status', { quotationIds, status }),
  bulkDelete: (quotationIds) => api.delete('/quotations/bulk', { data: { quotationIds } }),
};

// Invoice API
export const invoiceAPI = {
  create: (invoiceData) => api.post('/invoices', invoiceData),
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  getMyInvoices: (params) => api.get('/invoices/my-invoices', { params }),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  getStats: () => api.get('/invoices/stats'),
  // Bulk operations
  bulkUpdateStatus: (invoiceIds, status) => api.put('/invoices/bulk/status', { invoiceIds, status }),
  bulkDelete: (invoiceIds) => api.delete('/invoices/bulk', { data: { invoiceIds } }),
};

// Quotation & Invoice Defaults API
export const quotationInvoiceDefaultsAPI = {
  get: () => api.get('/quotation-invoice-defaults'),
  update: (data) => api.put('/quotation-invoice-defaults', data),
};



// EMI/Installment API
export const emiAPI = {
  create: (emiData) => api.post('/emis', emiData),
  getAll: (params) => api.get('/emis', { params }),
  getById: (id) => api.get(`/emis/${id}`),
  update: (id, data) => api.put(`/emis/${id}`, data),
  delete: (id) => api.delete(`/emis/${id}`),
  getMyEmis: (params) => api.get('/emis/my-emis', { params }),
  getStats: () => api.get('/emis/stats'),
};

// Payment Transactions API
export const paymentTransactionsAPI = {
  getAll: (params) => api.get('/payment-transactions', { params }),
  getById: (id) => api.get(`/payment-transactions/${id}`),
  create: (data) => api.post('/payment-transactions', data),
  update: (id, data) => api.put(`/payment-transactions/${id}`, data),
  updateUser: (id, data) => api.put(`/payment-transactions/user/${id}`, data),
  delete: (id) => api.delete(`/payment-transactions/${id}`),
  getUserTransactions: (params) => api.get('/payment-transactions/user', { params }),
  getStats: () => api.get('/payment-transactions/stats/summary'),
};



// File/Document API
export const fileAPI = {
  upload: (fileData) => api.post('/files/upload', fileData),
  getAll: (params) => api.get('/files', { params }),
  getById: (id) => api.get(`/files/${id}`),
  update: (id, data) => api.put(`/files/${id}`, data),
  delete: (id) => api.delete(`/files/${id}`),
  download: (id) => api.get(`/files/${id}/download`, { responseType: 'blob' }),
  getMyFiles: (params) => api.get('/files/my-files', { params }),
  getStats: () => api.get('/files/stats'),
};

// Analytics API
export const analyticsAPI = {
  getDashboardStats: () => api.get('/analytics/dashboard'),
  getProjectAnalytics: (projectId) => api.get(`/analytics/projects/${projectId}`),
  getFinancialAnalytics: (params) => api.get('/analytics/financial', { params }),
  getActivityAnalytics: (params) => api.get('/analytics/activity', { params }),
};

// WhatsApp Templates API
export const whatsappTemplatesAPI = {
  // Get all templates with filters
  getTemplates: (params = {}) => 
    api.get('/whatsapp-templates', { params }),
  
  // Get template by ID
  getTemplate: (id) => 
    api.get(`/whatsapp-templates/${id}`),
  
  // Create new template
  createTemplate: (data) => 
    api.post('/whatsapp-templates', data),
  
  // Update template
  updateTemplate: (id, data) => 
    api.put(`/whatsapp-templates/${id}`, data),
  
  // Delete template
  deleteTemplate: (id) => 
    api.delete(`/whatsapp-templates/${id}`),
  
  // Submit template to Meta
  submitTemplate: (id) => 
    api.post(`/whatsapp-templates/${id}/submit`),
  
  // Get template categories
  getCategories: () => 
    api.get('/whatsapp-templates/categories/all'),
  
  // Load templates from Meta WhatsApp API
  loadFromMeta: () => 
    api.post('/whatsapp-templates/load-from-meta'),
  
  // Get Meta WhatsApp Template Management URL
  getMetaManagementUrl: () => 
    api.get('/whatsapp-templates/meta-management-url'),
  
  // AI Template Generation
  generateAITemplate: (data) => 
    api.post('/whatsapp-templates/ai/generate', data),
  
  // AI Template Optimization
  optimizeTemplate: (data) => 
    api.post('/whatsapp-templates/ai/optimize', data),
  
  // Multi-language template generation
  generateTranslations: (data) => 
    api.post('/whatsapp-templates/ai/translate', data),
  
  // Industry-specific templates
  generateIndustryTemplates: (data) => 
    api.post('/whatsapp-templates/ai/industry', data),
  
  // Bulk operations
  bulkDelete: (templateIds) => 
    api.post('/whatsapp-templates/bulk/delete', { templateIds }),
  
  bulkUpdateStatus: (templateIds, status) => 
    api.post('/whatsapp-templates/bulk/status', { templateIds, status })
};

// Export default api instance
export default api;
